// ApplyForInclusionPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The canonical membership application page. Replaces /list-your-business.
//
// Phase 1 (this commit): orchestrator + cinematic hero + the three proof
// sections (Inside the Collection, Edit Showcase, Member Voices). The form
// section is a placeholder anchor for now, the full ApplicationForm is
// landing in Phase 2 with the same data flow as the existing
// `listing_applications` table + `sendApplicationConfirmation` edge function,
// so the admin's ListingApplicationsModule + CRM lead views automatically
// receive submissions with no module changes needed.
//
// Visual direction:
//   • Cinematic hero (dark, full-bleed photograph)
//   • Cream editorial body
//   • Dark Edit showcase section in the middle (mood shift)
//   • Gold hairlines, no gimmicks, no SaaS pricing boxes
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import HomeNav from '../../components/nav/HomeNav.jsx';

import CinematicHero        from './sections/CinematicHero.jsx';
import InsideTheCollection  from './sections/InsideTheCollection.jsx';
import AuraShowcase          from './sections/AuraShowcase.jsx';
import EditShowcase          from './sections/EditShowcase.jsx';
import PublicationsShowcase  from './sections/PublicationsShowcase.jsx';
import AuthorityStatement    from './sections/AuthorityStatement.jsx';
import MemberVoices          from './sections/MemberVoices.jsx';
import ApplicationForm       from './sections/ApplicationForm.jsx';
import { getPalette }        from './data/inclusionContent.js';

const NU   = 'var(--font-body)';

export default function ApplyForInclusionPage({
  onToggleDark, darkMode, onVendorLogin, onNavigateStandard, onNavigateAbout,
}) {
  const formRef = useRef(null);
  // ── DARK BY DEFAULT (per brief) ──────────────────────────────────────
  // Apply for Inclusion is a single editorial document. Its rhythm,
  // alternating cream and dark, only works when read in the order it
  // was designed for, and the dark version reads as private and
  // invitation-only where the cream version reads as a "platform".
  //
  // So the page opens in dark regardless of the user's site-wide
  // preference. If they explicitly toggle the lights while on this page,
  // we respect that and follow the global setting for the remainder of
  // the visit. We only override the *initial* impression.
  const initialGlobalDark = useRef(darkMode);
  const [hasToggledOnPage, setHasToggledOnPage] = useState(false);
  useEffect(() => {
    if (darkMode !== initialGlobalDark.current) setHasToggledOnPage(true);
  }, [darkMode]);
  const pageDarkMode = hasToggledOnPage ? darkMode : true;
  const P = getPalette(pageDarkMode);

  const scrollToForm = () => {
    if (!formRef.current) return;
    // Account for the fixed HomeNav so the section heading lands cleanly
    // below it (matches the pattern from PricingPage / list-your-business).
    const navEl = document.querySelector('.home-nav');
    const navHeight = navEl ? navEl.getBoundingClientRect().height : 100;
    const top = formRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 24;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: P.pageBg,
      color: P.text,
      fontFamily: NU,
      transition: 'background 0.3s ease, color 0.3s ease',
    }}>
      <Helmet>
        <title>Apply for Inclusion, LWD</title>
        <meta name="description" content="A curated collection of the world's most exceptional wedding venues, vendors and planners. Apply for membership in the Luxury Wedding Directory." />
        <meta property="og:type"  content="website" />
        <meta property="og:title" content="Apply for Inclusion, LWD" />
      </Helmet>

      <HomeNav
        onToggleDark={onToggleDark}
        darkMode={darkMode}
        onVendorLogin={onVendorLogin}
        onNavigateStandard={onNavigateStandard}
        onNavigateAbout={onNavigateAbout}
      />

      {/* 1. Cinematic Hero */}
      <CinematicHero scrollToForm={scrollToForm} />

      {/* Phase 2 sections (Promise / Standard / Reach / Process / Investment)
          will mount here. Phase 1 ships the proof sections only, they do
          the heavy conversion lifting. */}

      {/* 2. Inside the Collection (live featured members) */}
      <InsideTheCollection darkMode={pageDarkMode} />

      {/* 3. Aura, the LWD discovery engine. Always-dark by design.
             This is the unfair advantage on the discovery side; The LWD Edit
             below is the unfair advantage on the editorial side. Together
             they make the case that LWD is intelligent matching + story,
             not just a directory. */}
      <AuraShowcase />

      {/* 4. Where Your Work Becomes Editorial (live Edit posts).
             Always-dark by design, like a magazine spread inside the
             brochure. Pairs with Aura above as LWD's two unfair advantages. */}
      <EditShowcase />

      {/* 4. The Magazines (live magazine_issues, 3-4 most recent published) */}
      <PublicationsShowcase darkMode={pageDarkMode} />

      {/* 5. Authority Statement, the quiet, weighted positional beat that
              answers the unspoken vendor question: "will I be one of many,
              or one of few?". Sits between Publications and Voices to
              frame scarcity right before the page closes the funnel. */}
      <AuthorityStatement darkMode={pageDarkMode} />

      {/* 6. Voices from the Collection (real testimonials only, hidden until
              real quotes are added to inclusionContent.js) */}
      <MemberVoices darkMode={pageDarkMode} />

      {/* 7. Application form, always-dark by design, regardless of the
              lights toggle. Closing weight in the editorial rhythm:
              hero (dark) → collection (theme) → aura (dark) → edit (dark)
              → publications (theme) → authority (theme) → voices (theme)
              → form (dark). Full admin wiring lives in the component
              (listing_applications + leads + autoresponder + optional
              log; failed email never blocks). */}
      <ApplicationForm formRef={formRef} />
    </div>
  );
}
